import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertBanner, Button, Modal, Skeleton } from '@ministry/ui/internal';

import { API_BASE_URL, ApiError, NetworkError } from '../lib/api-client.js';
import { attachmentPreviewKind, fetchAttachmentPreviewBlob, type AttachmentRow } from '../lib/files.js';
import { useT } from '../i18n/strings.js';

export interface AttachmentPreviewModalProps {
  attachment: AttachmentRow;
  onOpenChange: (open: boolean) => void;
}

type T = (key: string, vars?: Record<string, string | number>) => string;

function errorContent(t: T, error: unknown): { title: string; body?: string } {
  if (error instanceof ApiError) {
    if (error.code === 'NOT_FOUND') return { title: t('fileDetail.preview.error.notFound') };
    if (error.code.startsWith('FORBIDDEN')) return { title: t('fileDetail.preview.error.forbidden') };
    if (error.retryable) return { title: t('fileDetail.preview.error.network') };
    return { title: t('fileDetail.preview.error.generic') };
  }
  if (error instanceof NetworkError) return { title: t('fileDetail.preview.error.network') };
  return { title: t('fileDetail.preview.error.generic') };
}

/**
 * The user's own words: "not view option of document." Renders a clean PDF or raster
 * image INLINE, next to (never instead of) the plain download link — coordinator's brief.
 *
 * **Security bar, verified against this exact implementation:**
 * - `attachmentPreviewKind` (`lib/files.ts`) is the ONE allowlist gate: PDF and raster
 *   images only, `image/svg+xml` excluded by name (script-capable), fail-closed on
 *   anything not `attached && scanState === 'clean'` — identical to the download link's
 *   own gate, never looser.
 * - No `<iframe srcdoc>`. The PDF path uses `<embed type="application/pdf">` against an
 *   in-page `Blob` object URL this client constructed itself with an explicit
 *   `application/pdf` MIME type (never whatever the server's `Content-Type` header says —
 *   the download endpoint always answers `application/octet-stream` by design, see
 *   `fetchAttachmentPreviewBlob`'s own comment). No HTML is ever parsed as markup here:
 *   `<embed>` hands the bytes to the browser's native PDF renderer, `<img>` hands raster
 *   bytes to the browser's native image decoder — neither executes script from the file.
 * - Not previewable → download-only, plainly stated, never a dead control.
 */
export function AttachmentPreviewModal({ attachment, onOpenChange }: AttachmentPreviewModalProps): React.JSX.Element {
  const t = useT();
  const kind = attachmentPreviewKind(attachment);

  const query = useQuery({
    queryKey: ['attachment-preview', attachment.id],
    queryFn: ({ signal }) => fetchAttachmentPreviewBlob(attachment.id, attachment.mimeType, signal),
    enabled: kind !== null,
  });

  // Revoked on every change AND on unmount — an object URL kept alive past this modal's
  // life is a real memory leak in a screen an officer opens dozens of times a shift.
  const objectUrl = React.useMemo(() => (query.data ? URL.createObjectURL(query.data) : null), [query.data]);
  React.useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const downloadHref = `${API_BASE_URL}/attachments/${attachment.id}/download`;

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title={attachment.originalFilename}
      cancelLabel={t('fileDetail.preview.close')}
      size="large"
    >
      <div className="flex h-full min-h-0 flex-col gap-3">
        <div className="flex shrink-0 items-center justify-between gap-3">
          <p className="text-sm text-text-muted">{t('fileDetail.preview.downloadHint')}</p>
          <a
            href={downloadHref}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-sm font-semibold text-text-link underline"
          >
            {t('fileDetail.attachmentDownload')}
          </a>
        </div>

        {kind === null ? (
          <AlertBanner variant="info" title={t('fileDetail.preview.unsupported.title')}>
            {t('fileDetail.preview.unsupported.body')}
          </AlertBanner>
        ) : query.isLoading ? (
          <Skeleton shape="block" className="h-[60vh] w-full" />
        ) : query.isError ? (
          (() => {
            const { title, body } = errorContent(t, query.error);
            return (
              <AlertBanner
                variant="critical"
                title={title}
                action={
                  <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
                    {t('common.retry')}
                  </Button>
                }
              >
                {body}
              </AlertBanner>
            );
          })()
        ) : objectUrl && kind === 'pdf' ? (
          <embed
            src={objectUrl}
            type="application/pdf"
            title={t('fileDetail.preview.pdfTitle', { name: attachment.originalFilename })}
            className="h-[70vh] w-full rounded-md border border-border-decorative"
          />
        ) : objectUrl && kind === 'image' ? (
          <img
            src={objectUrl}
            alt={t('fileDetail.preview.imageAlt', { name: attachment.originalFilename })}
            className="max-h-[70vh] w-full rounded-md border border-border-decorative object-contain"
          />
        ) : null}
      </div>
    </Modal>
  );
}
